/**
 * `apiFetch` — the single HTTP client every dashboard-v2 hook funnels
 * through.
 *
 * Ported from `apps/dashboard/src/lib/api.ts` (legacy Astro app) but
 * stripped of its inline redirect behaviour. In v2 the redirects are
 * centralised in the `QueryCache` / `MutationCache` error handler on
 * the root `QueryClient` — see `./query-client.ts`. Rationale:
 *   1. The fetch layer has no router — it can't `navigate({to})`
 *      without reaching for `window.location.assign`, which does a
 *      full reload and loses in-flight Clerk state.
 *   2. Some call sites (the `/__data` debug page, future Playwright
 *      probes) need to distinguish "401" from "redirect to login". A
 *      classifier at the cache level handles the happy path while
 *      leaving throw-through behaviour intact for consumers that
 *      opt-out.
 *
 * Call contract:
 *   - JWT is read from Clerk via the global `Clerk.session.getToken()`.
 *     Works outside React (no hook). Returns `null` when signed out —
 *     the call still goes through, the API returns 401, and the query
 *     cache handler signs out + redirects.
 *   - `X-Request-Id` is generated client-side (`req_<nanoid-ish>`) so
 *     logs on apps/api carry the same id users can copy out of a
 *     toast.
 *   - `credentials: 'omit'` — bearer-only, matches apps/api's CORS
 *     policy (see essentials.md §1). Do NOT flip to 'include' without
 *     a CSRF layer.
 *   - `meta.upgrade_url` (402) and `meta.feature` are preserved
 *     verbatim. The billing prompt store reads them — see
 *     `./stores/billing-prompt.ts` + `./query-client.ts`.
 *
 * NOT a server-side helper. This module is SPA-only.
 */

/** Shape of the `error` object inside every non-2xx JSON response. */
export interface ApiErrorShape {
  code?: string;
  message?: string;
  request_id?: string;
  details?: Array<{ path?: string; issue: string }>;
  meta?: Record<string, unknown>;
}

/**
 * Thrown for every non-2xx response. Preserves the full server error
 * envelope so the cache error handler can branch on `status` + `code`
 * + `meta`.
 *
 * `instanceof FetchError` is the contract for every `onError` handler
 * downstream — never catch a raw `Error` and try to sniff status.
 */
export class FetchError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly request_id?: string;
  readonly details?: Array<{ path?: string; issue: string }>;
  readonly meta?: Record<string, unknown>;

  constructor(status: number, payload: unknown) {
    const errorField = (payload as { error?: ApiErrorShape } | null | undefined)?.error;
    super(errorField?.message ?? `Request failed (${status})`);
    this.name = 'FetchError';
    this.status = status;
    if (errorField?.code !== undefined) this.code = errorField.code;
    if (errorField?.request_id !== undefined) this.request_id = errorField.request_id;
    if (errorField?.details !== undefined) this.details = errorField.details;
    if (errorField?.meta !== undefined) this.meta = errorField.meta;
  }
}

/**
 * Options accepted by `apiFetch`. Extends `RequestInit` but narrows
 * `headers` to a plain string record (the superset `HeadersInit` is
 * awkward to merge).
 */
export interface ApiFetchOptions extends Omit<RequestInit, 'headers' | 'body'> {
  headers?: Record<string, string>;
  /**
   * Request body. If it's a plain object, we JSON-encode and set
   * `Content-Type: application/json`. Pass a string / FormData /
   * Blob to opt out of encoding.
   */
  body?: unknown;
  /**
   * Idempotency-Key to forward as a header (24h dedup on apps/api
   * `POST /v1/audits`). No-op on other endpoints.
   */
  idempotencyKey?: string;
}

/**
 * Clerk global — populated by `@clerk/react`'s `ClerkProvider`
 * during boot. `getToken()` is safe to call before Clerk finishes
 * initialising (returns null) and after sign-out (returns null).
 *
 * Typed loosely because Clerk's global surface isn't in
 * `@clerk/shared/types`.
 */
interface ClerkGlobal {
  session?: {
    getToken?: () => Promise<string | null>;
  } | null;
}

async function resolveBearerToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  const clerk = (window as unknown as { Clerk?: ClerkGlobal }).Clerk;
  const getToken = clerk?.session?.getToken;
  if (typeof getToken !== 'function') return null;
  try {
    return await getToken();
  } catch {
    return null;
  }
}

/**
 * Small random request id. Mirrors the `req_<12 chars>` format
 * apps/api emits so copy-paste into grep Just Works. Not
 * cryptographically meaningful — collision risk at dashboard traffic
 * levels is negligible.
 */
function newRequestId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  return `req_${Array.from(bytes, (b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, 12)}`;
}

function resolveBaseUrl(): string {
  const raw = import.meta.env.PUBLIC_API_BASE_URL ?? '';
  // Strip trailing slash so `${base}${path}` where path starts with
  // `/` never produces `//v1/...`.
  return raw.replace(/\/+$/, '');
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function encodeBody(body: unknown, headers: Record<string, string>): BodyInit | undefined {
  if (body === undefined || body === null) return undefined;
  if (typeof body === 'string') return body;
  if (body instanceof FormData) return body;
  if (body instanceof Blob) return body;
  if (body instanceof ArrayBuffer) return body;
  if (body instanceof URLSearchParams) return body;
  // Assume JSON.
  if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
  return JSON.stringify(body);
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { headers: extraHeaders = {}, body, idempotencyKey, ...rest } = options;

  const headers: Record<string, string> = {
    'X-Request-Id': newRequestId(),
    Accept: 'application/json',
    ...extraHeaders,
  };

  const token = await resolveBearerToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  const encoded = encodeBody(body, headers);
  const url = path.startsWith('http') ? path : `${resolveBaseUrl()}${path}`;

  const res = await fetch(url, {
    ...rest,
    headers,
    credentials: 'omit',
    ...(encoded !== undefined ? { body: encoded } : {}),
  });

  // 204 No Content — Mutations that delete / revoke return this.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeJson(text) : undefined;

  if (res.ok) return payload as T;

  throw new FetchError(res.status, payload);
}
